import { getErrorMessage } from "../utils/errorMessage";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { revenueAPI, projectsAPI, customersAPI } from "../api/client";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import { normalizeRole } from "../utils/accessControl.js";
import { mayPerform, mayPerformWithRole } from "../utils/userAccess.js";
import "./RevenueTab.css";
import ConfirmModal from "../components/ConfirmModal";
import { useMasters } from "../context/MastersContext";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - 3 + i);

const LEVEL_OPTIONS = [
  { value: "project", label: "By Project" },
  { value: "customer", label: "By Customer" },
  { value: "bu", label: "By Business Unit" },
  { value: "projectType", label: "By Project Type" },
  { value: "company", label: "By Company" },
];

const ADJUSTMENT_REASONS = [
  "Estimate vs Actual billing difference",
  "Product & IP sales",
  "Consulting revenue",
  "% Completion for Fixed price",
  "Expense billing",
  "Missing timesheet",
  "Missing PO/SOW/Rate issue",
  "Others",
];

function fmtCurrency(val) {
  const num = Number(val) || 0;
  return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPct(val) {
  const num = Number(val) || 0;
  return num.toFixed(1) + "%";
}

export default function RevenueTab() {
  const { getList } = useMasters();
  const ADJUSTMENT_REASONS = getList("revenue_adjustment_reason");
  const [year, setYear] = useState(CURRENT_YEAR);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [level, setLevel] = useState("project");
  const [company, setCompany] = useState("");
  const [companyOptions, setCompanyOptions] = useState([]);
  const [projects, setProjects] = useState([]);

  const [summary, setSummary] = useState([]);
  const [freezeStatus, setFreezeStatus] = useState({ frozen: false });
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [freezing, setFreezing] = useState(false);
  const [confirmFreeze, setConfirmFreeze] = useState(false);
  const [exporting, setExporting] = useState(false);

  const canEdit = useMemo(() => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return false;
      const decoded = jwtDecode(token);
      const role = normalizeRole(decoded?.role || "");
      return role !== "business_user" && mayPerform("resource_cost", "edit");
    } catch { return false; }
  }, []);
  const canExport = useMemo(() => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return false;
      const decoded = jwtDecode(token);
      const role = normalizeRole(decoded?.role || "");
      return mayPerformWithRole(role, "resource_cost", "export", "resource_cost.export");
    } catch { return false; }
  }, []);

  // Drill-down
  const [drillProject, setDrillProject] = useState(null);
  const [drillData, setDrillData] = useState([]);
  const [drillAdjustments, setDrillAdjustments] = useState(0);
  const [drillLoading, setDrillLoading] = useState(false);

  // Adjustments
  const [adjustments, setAdjustments] = useState([]);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [adjustForm, setAdjustForm] = useState({
    projectId: "", customerId: "", businessUnit: "",
    adjustmentAmount: "", reason: "", remarks: "",
  });

  /* --- Fetch customers (Customer Master single source of truth) --- */
  useEffect(() => {
    customersAPI.getAll().then((res) => {
      setCustomers(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
  }, []);

  /* --- Fetch projects & company options --- */
  useEffect(() => {
    projectsAPI.getAll({ limit: 1000 }).then((res) => {
      const allProjects = res.data?.projects || [];
      setProjects(allProjects);
      const entities = [...new Set(allProjects.map((p) => p.uts_billing_entity).filter(Boolean))];
      setCompanyOptions(entities);
    }).catch(() => {});
  }, []);

  /* --- Fetch summary + freeze --- */
  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const params = { year, month, level };
      if (company) params.company = company;
      const [summaryRes, freezeRes] = await Promise.all([
        revenueAPI.getSummary(params),
        revenueAPI.getFreezeStatus({ year, month }),
      ]);
      setSummary(Array.isArray(summaryRes.data) ? summaryRes.data : []);
      setFreezeStatus(freezeRes.data || { frozen: false });
    } catch (err) {
      console.error("Failed to fetch revenue summary:", err);
      toast.error("Failed to load revenue data");
    } finally {
      setLoading(false);
    }
  }, [year, month, level, company]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  /* --- Calculate --- */
  const handleCalculate = async () => {
    setCalculating(true);
    try {
      const res = await revenueAPI.calculate({ year, month });
      toast.success(`Calculated: ${res.data.revenueEntries} revenue, ${res.data.costEntries} cost entries`);
      await fetchSummary();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to calculate"));
    } finally {
      setCalculating(false);
    }
  };

  /* --- Freeze --- */
  const handleFreeze = () => {
    setConfirmFreeze(true);
  };

  const doFreeze = async () => {
    setFreezing(true);
    try {
      await revenueAPI.freezeMonth({ year, month });
      toast.success("Month frozen successfully");
      setConfirmFreeze(false);
      await fetchSummary();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to freeze"));
    } finally {
      setFreezing(false);
    }
  };

  /* --- Export --- */
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = { year, month, level };
      if (company) params.company = company;
      const res = await revenueAPI.exportSummary(params);
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `revenue_summary_${MONTHS[month - 1]}_${year}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (err) {
      console.error("Export failed", err);
      toast.error("Failed to export revenue data");
    } finally {
      setExporting(false);
    }
  };

  /* --- Drill-down --- */
  const handleDrillDown = async (row) => {
    setDrillProject(row);
    setDrillLoading(true);
    try {
      const projectId = row.project_id || row.id;
      const res = await revenueAPI.getResourceDetails({ year, month, projectId });
      const data = res.data || {};
      setDrillData(Array.isArray(data.resources) ? data.resources : []);
      setDrillAdjustments(Number(data.projectAdjustments) || 0);
    } catch (err) {
      toast.error("Failed to load resource details");
    } finally {
      setDrillLoading(false);
    }
  };

  /* --- Adjustments --- */
  const fetchAdjustments = async () => {
    try {
      const res = await revenueAPI.getAdjustments({ year, month });
      setAdjustments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch adjustments");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAdjustments(); }, [year, month]);

  const handleAdjustSubmit = async (e) => {
    e.preventDefault();
    if (!adjustForm.reason) {
      toast.error("Reason is required");
      return;
    }
    try {
      await revenueAPI.addAdjustment({
        year,
        month,
        projectId: adjustForm.projectId || undefined,
        customerId: adjustForm.customerId || undefined,
        businessUnit: adjustForm.businessUnit || undefined,
        adjustmentAmount: Number(adjustForm.adjustmentAmount) || 0,
        reason: adjustForm.reason,
        remarks: adjustForm.remarks,
      });
      toast.success("Adjustment added");
      setShowAdjustModal(false);
      setAdjustForm({ projectId: "", customerId: "", businessUnit: "", adjustmentAmount: "", reason: "", remarks: "" });
      await fetchSummary();
      await fetchAdjustments();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to add adjustment"));
    }
  };

  /* --- Totals --- */
  const totals = summary.reduce(
    (acc, r) => ({
      revenue: acc.revenue + Number(r.calculatedRevenue || 0),
      cost: acc.cost + Number(r.totalCost || 0),
      adjustments: acc.adjustments + Number(r.totalAdjustments || 0),
      finalRevenue: acc.finalRevenue + Number(r.finalRevenue || 0),
    }),
    { revenue: 0, cost: 0, adjustments: 0, finalRevenue: 0 }
  );
  totals.profit = totals.finalRevenue - totals.cost;
  totals.margin = totals.finalRevenue > 0 ? (totals.profit / totals.finalRevenue) * 100 : 0;

  /* ============================================================
     RENDER
     ============================================================ */
  return (
    <div className="rev-tab">

      {/* HEADER */}
      <div className="rev-header">
        <div>
          <h2>Revenue Management</h2>
          <p>Calculate, adjust, and freeze monthly revenue and cost</p>
        </div>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <span className={`rev-freeze-badge ${freezeStatus.frozen ? "frozen" : "open"}`}>
            {freezeStatus.frozen ? "\u{1F512} Frozen" : "\u{1F513} Open"}
          </span>
        </div>
      </div>

      {/* TOOLBAR */}
      <div className="rev-toolbar">
        <div className="rev-toolbar-filters">
          <div className="rev-filter-group">
            <label htmlFor="revenue-year">Year</label>
            <select id="revenue-year" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          <div className="rev-filter-group">
            <label htmlFor="revenue-month">Month</label>
            <select id="revenue-month" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          </div>

          <div className="rev-filter-group">
            <label htmlFor="revenue-company">Company</label>
            <select id="revenue-company" value={company} onChange={(e) => setCompany(e.target.value)}>
              <option value="">All Companies</option>
              {companyOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div className="rev-toolbar-sep" />

        <div className="rev-toolbar-actions">
          <button
            type="button"
            className="rev-btn-primary"
            onClick={handleCalculate}
            disabled={!canEdit || calculating || freezeStatus.frozen}
          >
            {calculating ? "Calculating..." : "\u2699 Calculate Revenue"}
          </button>

          <button
            type="button"
            className="rev-btn-danger"
            onClick={handleFreeze}
            disabled={!canEdit || freezing || freezeStatus.frozen}
          >
            {freezing ? "Freezing..." : "\u{1F512} Freeze Month"}
          </button>

          <button
            type="button"
            className="rev-btn-secondary"
            onClick={handleExport}
            disabled={!canExport || exporting || summary.length === 0}
          >
            {exporting ? "Exporting..." : "\u{1F4E5} Export Excel"}
          </button>
        </div>
      </div>

      {/* SUMMARY CARDS */}
      <div className="rev-summary-cards">
        <div className="rev-summary-card">
          <div className="label">Total Revenue</div>
          <div className="value">{fmtCurrency(totals.revenue)}</div>
        </div>
        <div className="rev-summary-card">
          <div className="label">Total Cost</div>
          <div className="value">{fmtCurrency(totals.cost)}</div>
        </div>
        <div className="rev-summary-card">
          <div className="label">Adjustments</div>
          <div className="value">{fmtCurrency(totals.adjustments)}</div>
        </div>
        <div className="rev-summary-card">
          <div className="label">Final Revenue</div>
          <div className="value">{fmtCurrency(totals.finalRevenue)}</div>
        </div>
        <div className="rev-summary-card">
          <div className="label">Profit</div>
          <div className={`value ${totals.profit >= 0 ? "profit" : "loss"}`}>{fmtCurrency(totals.profit)}</div>
        </div>
        <div className="rev-summary-card">
          <div className="label">Margin</div>
          <div className={`value ${totals.margin >= 0 ? "profit" : "loss"}`}>{fmtPct(totals.margin)}</div>
        </div>
      </div>

      {/* LEVEL SWITCHER */}
      {!drillProject && (
        <div className="rev-level-switcher">
          {LEVEL_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`rev-level-btn ${level === opt.value ? "active" : ""}`}
              onClick={() => setLevel(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* DRILL-DOWN or SUMMARY TABLE */}
      {drillProject ? (
        /* --- Resource Detail View --- */
        <div className="rev-section">
          <div className="rev-drilldown-header">
            <h3>
              Resources &mdash; {drillProject.projectCode || drillProject.projectDescription || `Project ${drillProject.project_id}`}
            </h3>
            <button type="button" className="rev-btn-secondary" onClick={() => { setDrillProject(null); setDrillData([]); }}>
              &larr; Back to Summary
            </button>
          </div>
          <div className="rev-table-wrapper">
            {drillLoading ? (
              <div className="rev-loading"><div className="rev-spinner" /><p>Loading resources...</p></div>
            ) : drillData.length === 0 ? (
              <div className="rev-empty"><h3>No resources found</h3><p>No resource-level data for this project and month.</p></div>
            ) : (
              <table className="rev-table">
                <thead>
                  <tr>
                    <th scope="col">Employee ID</th>
                    <th scope="col">Billing Rate</th>
                    <th scope="col">Rate Type</th>
                    <th scope="col">Allocation %</th>
                    <th scope="col">Working Hours</th>
                    <th scope="col" style={{ textAlign: "right" }}>Calculated Revenue</th>
                    <th scope="col" style={{ textAlign: "right" }}>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {drillData.map((r, i) => (
                    <tr key={i}>
                      <td>{r.employeeId || "\u2014"}</td>
                      <td>{fmtCurrency(r.billingRate)}</td>
                      <td>{r.billingRateType || "\u2014"}</td>
                      <td>{r.allocation}%</td>
                      <td>{r.billingRateType === "Hourly" || r.billingRateType === "Hourly rate" ? 160 : r.billingRateType === "Daily" || r.billingRateType === "Daily rate" ? 20 : r.billingRateType === "Monthly" || r.billingRateType === "Monthly rate" ? 1 : 0}</td>
                      <td className="rev-num">{fmtCurrency(r.calculatedRevenue)}</td>
                      <td className="rev-num">{fmtCurrency(r.calculatedCost)}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: "2px solid #333", fontWeight: "bold" }}>
                    <td colSpan={5}>Project Resources Total</td>
                    <td className="rev-num">{fmtCurrency(drillData.reduce((s, r) => s + Number(r.calculatedRevenue || 0), 0))}</td>
                    <td className="rev-num">{fmtCurrency(drillData.reduce((s, r) => s + Number(r.calculatedCost || 0), 0))}</td>
                  </tr>
                  {drillAdjustments !== 0 && (
                    <tr style={{ borderTop: "2px dashed #333" }}>
                      <td colSpan={5}>Project Adjustments</td>
                      <td className="rev-num">{fmtCurrency(drillAdjustments)}</td>
                      <td></td>
                    </tr>
                  )}
                  <tr style={{ borderTop: "2px solid #333", fontWeight: "bold" }}>
                    <td colSpan={5}>Final Project Revenue</td>
                    <td className="rev-num" style={{ fontSize: "14px" }}>{fmtCurrency(drillData.reduce((s, r) => s + Number(r.calculatedRevenue || 0), 0) + drillAdjustments)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : (
        /* --- Summary Table --- */
        <div className="rev-section">
          <div className="rev-section-header">
            <h3>
              {LEVEL_OPTIONS.find((l) => l.value === level)?.label || "Summary"} &mdash; {MONTHS[month - 1]} {year}
            </h3>
            <button type="button" className="rev-btn-secondary" onClick={() => setShowAdjustModal(true)} disabled={!canEdit || freezeStatus.frozen}>
              + Add Adjustment
            </button>
          </div>
          <div className="rev-table-wrapper">
            {loading ? (
              <div className="rev-loading"><div className="rev-spinner" /><p>Loading revenue data...</p></div>
            ) : summary.length === 0 ? (
              <div className="rev-empty">
                <div className="empty-icon">&#128176;</div>
                <h3>No revenue data</h3>
                <p>Click "Calculate Revenue" to generate data for {MONTHS[month - 1]} {year}.</p>
              </div>
            ) : (
              <table className="rev-table">
                <thead>
                  <tr>
                    {level === "project" && <th scope="col">Project Code</th>}
                    {level === "project" && <th scope="col">Description</th>}
                    {level === "customer" && <th scope="col">Customer</th>}
                    {level === "bu" && <th scope="col">Business Unit</th>}
                    {level === "projectType" && <th scope="col">Project Type</th>}
                    {level === "company" && <th scope="col">Company</th>}
                    <th scope="col" style={{ textAlign: "right" }}>Calculated Revenue</th>
                    <th scope="col" style={{ textAlign: "right" }}>Adjustments</th>
                    <th scope="col" style={{ textAlign: "right" }}>Final Revenue</th>
                    <th scope="col" style={{ textAlign: "right" }}>Cost</th>
                    <th scope="col" style={{ textAlign: "right" }}>Profit</th>
                    <th scope="col">Margin</th>
                    {level === "project" && <th scope="col">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {summary.map((r, i) => {
                    const profit = Number(r.profit) || 0;
                    const margin = Number(r.profitMargin) || 0;
                    return (
                      <tr key={i} className={level === "project" ? "clickable-row" : ""}>
                        {level === "project" && <td><strong>{r.projectCode || "N/A"}</strong></td>}
                        {level === "project" && <td>{r.projectDescription || "\u2014"}</td>}
                        {level === "customer" && <td>{r.customerName || "No Billing Customer"}</td>}
                        {level === "bu" && <td>{r.businessUnit || "\u2014"}</td>}
                        {level === "projectType" && <td><strong>{r.projectType || "Unspecified"}</strong></td>}
                        {level === "company" && <td><strong>{r.company || "\u2014"}</strong></td>}
                        <td className="rev-num">{fmtCurrency(r.calculatedRevenue)}</td>
                        <td className="rev-num">{Number(r.totalAdjustments) !== 0 ? fmtCurrency(r.totalAdjustments) : "\u2014"}</td>
                        <td className="rev-num"><strong>{fmtCurrency(r.finalRevenue)}</strong></td>
                        <td className="rev-num">{fmtCurrency(r.totalCost)}</td>
                        <td className={`rev-num ${profit >= 0 ? "rev-profit-positive" : "rev-profit-negative"}`}>
                          {fmtCurrency(profit)}
                        </td>
                        <td>
                          <span className={`rev-margin-badge ${margin >= 0 ? "positive" : "negative"}`}>
                            {fmtPct(margin)}
                          </span>
                        </td>
                        {level === "project" && (
                          <td>
                            <button
                              type="button"
                              className="rev-btn-secondary"
                              style={{ padding: "4px 10px", fontSize: "12px" }}
                              onClick={() => handleDrillDown(r)}
                            >
                              View Details
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ADJUSTMENTS LIST */}
      {!drillProject && adjustments.length > 0 && (
        <div className="rev-section">
          <div className="rev-section-header">
            <h3>Adjustments ({adjustments.length})</h3>
          </div>
          <div className="rev-table-wrapper">
            <table className="rev-table">
              <thead>
                <tr>
                  <th scope="col">Project</th>
                  <th scope="col">Customer</th>
                  <th scope="col">BU</th>
                  <th scope="col" style={{ textAlign: "right" }}>Amount</th>
                  <th scope="col">Reason</th>
                  <th scope="col">Remarks</th>
                  <th scope="col">Created By</th>
                </tr>
              </thead>
              <tbody>
                {adjustments.map((a, i) => (
                  <tr key={a.id || i}>
                    <td>{a.projectCode || "\u2014"}</td>
                    <td>{a.customerName || "\u2014"}</td>
                    <td>{a.business_unit || "\u2014"}</td>
                    <td className="rev-num">{fmtCurrency(a.adjustment_amount)}</td>
                    <td>{a.reason}</td>
                    <td>{a.remarks || "\u2014"}</td>
                    <td>{a.created_by || "\u2014"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ADJUSTMENT MODAL */}
      {showAdjustModal && (
        <div className="rev-modal-overlay" onClick={() => setShowAdjustModal(false)}>
          <div className="rev-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Add Revenue Adjustment</h3>
            <form onSubmit={handleAdjustSubmit}>
              <div className="rev-modal-body">
                <p>Add an adjustment for {MONTHS[month - 1]} {year}. This will affect the final revenue calculation.</p>
                <div className="rev-form-row">
                  <div className="rev-form-group">
                    <label>Project (optional)</label>
                    <select
                      value={adjustForm.projectId}
                      onChange={(e) => setAdjustForm({ ...adjustForm, projectId: e.target.value })}
                    >
                      <option value="">All Projects</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>{p.project_id} &mdash; {p.description || "No description"}</option>
                      ))}
                    </select>
                  </div>
                  <div className="rev-form-group">
                    <label>Customer (optional)</label>
                    <select
                      value={adjustForm.customerId}
                      onChange={(e) => setAdjustForm({ ...adjustForm, customerId: e.target.value })}
                    >
                      <option value="">All Customers</option>
                      {customers.map((c) => (
                        <option key={c.id} value={c.customerId}>{c.customerId} - {c.customerName}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="rev-form-row">
                  <div className="rev-form-group">
                    <label>Business Unit (optional)</label>
                    <input
                      type="text"
                      value={adjustForm.businessUnit}
                      onChange={(e) => setAdjustForm({ ...adjustForm, businessUnit: e.target.value })}
                      placeholder="e.g. BU01"
                    />
                  </div>
                  <div className="rev-form-group">
                    <label>Adjustment Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={adjustForm.adjustmentAmount}
                      onChange={(e) => setAdjustForm({ ...adjustForm, adjustmentAmount: e.target.value })}
                      placeholder="e.g. 5000"
                    />
                  </div>
                </div>
                <div className="rev-form-group">
                  <label>Reason *</label>
                  <select
                    value={adjustForm.reason}
                    onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })}
                    required
                  >
                    <option value="">Select reason...</option>
                    {ADJUSTMENT_REASONS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div className="rev-form-group">
                  <label>Remarks</label>
                  <textarea
                    rows={3}
                    value={adjustForm.remarks}
                    onChange={(e) => setAdjustForm({ ...adjustForm, remarks: e.target.value })}
                    placeholder="Additional notes..."
                  />
                </div>
              </div>
              <div className="rev-modal-actions">
                <button type="button" className="rev-btn-secondary" onClick={() => setShowAdjustModal(false)}>Cancel</button>
                <button type="submit" className="rev-btn-primary">Add Adjustment</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={confirmFreeze}
        variant="danger"
        title="Freeze month?"
        message={`Freeze ${MONTHS[month - 1]} ${year}? This cannot be undone.`}
        confirmLabel="Freeze"
        cancelLabel="Cancel"
        isLoading={freezing}
        loadingText="Freezing..."
        onConfirm={doFreeze}
        onCancel={() => setConfirmFreeze(false)}
      />
    </div>
  );
}
